import { useState, useEffect, useContext } from "react";
import apiClient from "../../services/apiClient";
import {
  Table,
  Typography,
  message,
  Tag,
  Row,
  Col,
  Button,
  Tabs,
  Card,
} from "antd";
import {
  AuditOutlined,
  UserOutlined,
  BankOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { ThemeContext } from "../../context/ThemeContext";
import "./AdminDashboard.css";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const UnifiedAuditLogs = () => {
  const { isDark } = useContext(ThemeContext);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [auditType, setAuditType] = useState("user");
  const [stats, setStats] = useState({
    totalLogs: 0,
    todayLogs: 0,
    thisWeekLogs: 0,
    thisMonthLogs: 0,
  });

  // Configuration for different audit types
  const auditConfigs = {
    user: {
      title: "User Audit Logs",
      description: "Track and monitor all system changes and user activities.",
      endpoint: "/admin/audit/all",
      baseUrl: process.env.REACT_APP_AUTH_URL || "",
      icon: <UserOutlined />,
      responseHandler: (response) => response.data.success ? response.data.data : null,
    },
    issuer: {
      title: "Master Issuer Audit Logs",
      description: "Track and monitor all changes to Master Issuer data.",
      endpoint: "/admin/audit/issuer",
      baseUrl: process.env.REACT_APP_BASE_URL || "",
      icon: <BankOutlined />,
      responseHandler: (response) => response.data.statusCode === 200 ? response.data.data : null,
    },
    isin: {
      title: "Master ISIN Audit Logs",
      description: "Track and monitor all changes to Master ISIN Variable data.",
      endpoint: "/admin/audit/all",
      baseUrl: process.env.REACT_APP_BASE_URL || "",
      icon: <FileTextOutlined />,
      responseHandler: (response) => response.data.statusCode === 200 ? response.data.data : null,
    },
  };

  const fetchAuditLogs = async (pageNum = page, size = pageSize, type = auditType) => {
    setLoading(true);
    try {
      const config = auditConfigs[type];
      const response = await apiClient.get(`${config.baseUrl}${config.endpoint}`, {
        params: { page: pageNum - 1, size },
      });

      const data = config.responseHandler(response);
      if (!data) {
        throw new Error("Failed to fetch audit logs");
      }

      setAuditLogs(data.content || []);
      setTotal(data.totalElements || 0);

      // Calculate stats
      calculateStats(data.content || []);
    } catch (err) {
      message.error(`Failed to fetch ${config.title}`);
      console.error("Audit logs fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (logs) => {
    const now = dayjs();
    const today = now.startOf('day');
    const weekStart = now.startOf('week');
    const monthStart = now.startOf('month');

    const todayCount = logs.filter(log =>
      dayjs(log.updatedAt).isAfter(today)
    ).length;

    const weekCount = logs.filter(log =>
      dayjs(log.updatedAt).isAfter(weekStart)
    ).length;

    const monthCount = logs.filter(log =>
      dayjs(log.updatedAt).isAfter(monthStart)
    ).length;

    setStats({
      totalLogs: logs.length,
      todayLogs: todayCount,
      thisWeekLogs: weekCount,
      thisMonthLogs: monthCount,
    });
  };

  useEffect(() => {
    fetchAuditLogs();
  }, [page, pageSize, auditType]);

  const formatChanges = (oldValues, newValues) => {
    const changes = [];
    const allKeys = new Set([...Object.keys(oldValues || {}), ...Object.keys(newValues || {})]);

    allKeys.forEach(key => {
      const oldValue = oldValues?.[key];
      const newValue = newValues?.[key];

      if (oldValue !== newValue) {
        changes.push({
          field: key,
          oldValue: oldValue,
          newValue: newValue,
        });
      }
    });

    return changes;
  };

  const formatEnumValue = (value) => {
    if (!value) return value;
    // Convert enum values like "TERTIARY_INDUSTRIES" to "Tertiary Industries"
    return value
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatFieldName = (fieldName) => {
    // Convert camelCase to readable format
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  const renderChanges = (oldValues, newValues) => {
    const changes = formatChanges(oldValues, newValues);

    return (
      <div style={{ padding: '16px', background: isDark ? '#1a1a1a' : '#fafafa', borderRadius: '8px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '12px'
        }}>
          {changes.map((change, index) => (
            <div
              key={index}
              style={{
                background: isDark ? '#2a2a2a' : '#fff',
                borderRadius: '6px',
                padding: '12px',
                border: `1px solid ${isDark ? '#444' : '#e8e8e8'}`,
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <Text strong style={{
                  color: isDark ? '#fff' : '#000',
                  fontSize: '12px',
                }}>
                  {formatFieldName(change.field)}
                </Text>
                <Tag size="small" color="processing" style={{ fontSize: '10px' }}>
                  Modified
                </Tag>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginBottom: '4px' }}>
                    Previous:
                  </Text>
                  <div style={{
                    display: 'inline-block',
                    background: isDark ? '#1a1a1a' : '#fff5f5',
                    color: '#d32f2f',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    border: `1px solid ${isDark ? '#333' : '#ffcdd2'}`,
                    maxWidth: '100%',
                    wordBreak: 'break-word'
                  }}>
                    {change.oldValue === null ? (
                      <Text type="secondary" style={{ fontSize: '11px' }}>null</Text>
                    ) : change.oldValue === '' ? (
                      <Text type="secondary" style={{ fontSize: '11px' }}>empty</Text>
                    ) : typeof change.oldValue === 'string' && change.oldValue.length > 30 ? (
                      <Text style={{ fontSize: '11px' }}>{formatEnumValue(change.oldValue.substring(0, 30))}...</Text>
                    ) : (
                      <Text style={{ fontSize: '11px' }}>{formatEnumValue(String(change.oldValue))}</Text>
                    )}
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: isDark ? '#666' : '#999',
                  fontSize: '14px',
                  padding: '0 4px'
                }}>
                  →
                </div>

                <div style={{ flex: 1 }}>
                  <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginBottom: '4px' }}>
                    Current:
                  </Text>
                  <div style={{
                    display: 'inline-block',
                    background: isDark ? '#1a1a1a' : '#f1f8e9',
                    color: '#2e7d32',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    border: `1px solid ${isDark ? '#333' : '#c8e6c9'}`,
                    maxWidth: '100%',
                    wordBreak: 'break-word'
                  }}>
                    {change.newValue === null ? (
                      <Text type="secondary" style={{ fontSize: '11px' }}>null</Text>
                    ) : change.newValue === '' ? (
                      <Text type="secondary" style={{ fontSize: '11px' }}>empty</Text>
                    ) : typeof change.newValue === 'string' && change.newValue.length > 30 ? (
                      <Text style={{ fontSize: '11px' }}>{formatEnumValue(change.newValue.substring(0, 30))}...</Text>
                    ) : (
                      <Text style={{ fontSize: '11px' }}>{formatEnumValue(String(change.newValue))}</Text>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const getColumns = () => {
    const baseColumns = [
      {
        title: "Summary",
        key: "summary",
        width: 200,
        render: (_, record) => {
          const changes = formatChanges(record.oldValues, record.newValues);
          return (
            <div>
              <Text style={{
                color: isDark ? '#fff' : '#000',
                fontSize: '13px',
                fontWeight: 500
              }}>
                {changes.length} {changes.length === 1 ? 'field' : 'fields'} modified
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {changes.slice(0, 2).map(c => formatFieldName(c.field)).join(', ')}
                {changes.length > 2 && ` +${changes.length - 2} more`}
              </Text>
            </div>
          );
        },
      },
      {
        title: "Updated By",
        dataIndex: "updatedBy",
        key: "updatedBy",
        width: 180,
        render: (updatedBy) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: isDark ? '#52c41a' : '#f6ffed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isDark ? '#fff' : '#52c41a'
            }}>
              <UserOutlined style={{ fontSize: '14px' }} />
            </div>
            <div>
              <Text style={{
                color: isDark ? '#fff' : '#000',
                fontSize: '13px',
                fontWeight: 500
              }}>
                {updatedBy}
              </Text>
            </div>
          </div>
        ),
      },
      {
        title: "Timestamp",
        dataIndex: "updatedAt",
        key: "updatedAt",
        width: 160,
        render: (updatedAt) => (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              background: isDark ? '#1a1a1a' : '#f8f9fa',
              border: `1px solid ${isDark ? '#333' : '#e9ecef'}`,
              borderRadius: '6px',
              padding: '8px 12px',
              fontSize: '12px'
            }}>
              <Text style={{
                color: isDark ? '#fff' : '#000',
                fontSize: '12px',
                fontWeight: 500
              }}>
                {dayjs(updatedAt).format("MMM DD, YYYY")}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {dayjs(updatedAt).format("HH:mm:ss")}
              </Text>
            </div>
          </div>
        ),
      },
    ];

    // Add specific columns based on audit type
    if (auditType === 'issuer') {
      baseColumns.unshift({
        title: "Issuer Name",
        key: "issuer",
        width: 200,
        render: (_, record) => {
          const issuer = record.oldValues?.issuer || record.newValues?.issuer || "N/A";
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: isDark ? '#1890ff' : '#e6f7ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isDark ? '#fff' : '#1890ff'
              }}>
                <BankOutlined style={{ fontSize: '14px' }} />
              </div>
              <div>
                <Text style={{
                  color: isDark ? '#fff' : '#000',
                  fontSize: '13px',
                  fontWeight: 500,
                }}>
                  {issuer}
                </Text>
              </div>
            </div>
          );
        },
      });
    } else if (auditType === 'isin') {
      baseColumns.unshift({
        title: "ISIN",
        key: "isin",
        width: 150,
        render: (_, record) => {
          const isin = record.oldValues?.isin || record.newValues?.isin || "N/A";
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: isDark ? '#722ed1' : '#f9f0ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isDark ? '#fff' : '#722ed1'
              }}>
                <FileTextOutlined style={{ fontSize: '14px' }} />
              </div>
              <div>
                <Text style={{
                  color: isDark ? '#fff' : '#000',
                  fontSize: '13px',
                  fontWeight: 500,
                  fontFamily: 'monospace'
                }}>
                  {isin}
                </Text>
              </div>
            </div>
          );
        },
      });
    }

    return baseColumns;
  };

  const config = auditConfigs[auditType];

  const renderAuditContent = () => (
    <div className={`admin-dashboard-container ${isDark ? 'dark' : ''}`}>
      {/* Hero Section */}
      <div
        className="audit-hero"
        style={{
          background: 'linear-gradient(90deg, #3AA015 0%, #F49A24 50%, rgba(255, 0, 4, 0.61) 100%)',
          borderRadius: 16,
          padding: "48px 48px 40px 48px",
          marginBottom: 32,
          boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 4px 20px rgba(0, 0, 0, 0.08)",
          position: 'relative',
          overflow: 'hidden',
          minHeight: 180,
        }}
      >
        {/* Overlay for better text readability */}
        {isDark && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 100%)',
              borderRadius: 16,
            }}
          />
        )}
        
        {/* Content */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <Title
            level={1}
            style={{
              margin: 0,
              marginBottom: 12,
              fontWeight: 800,
              fontSize: 42,
              color: isDark ? "#ffffff" : "#1a1a1a",
              textShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none',
            }}
          >
            {config.title}
          </Title>
          <Text 
            style={{ 
              fontSize: 20, 
              color: isDark ? "rgba(255, 255, 255, 0.9)" : "#4a4a4a",
              textShadow: isDark ? '0 1px 2px rgba(0, 0, 0, 0.3)' : 'none',
              fontWeight: 400,
              lineHeight: 1.4,
            }}
          >
            {config.description}
          </Text>
        </div>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} md={6}>
          <div
            style={{
              background: isDark ? "#2a2a3e" : "#fff",
              border: isDark ? "1px solid #3a3a4e" : "1px solid #000",
              borderRadius: 12,
              boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
              padding: "20px",
              position: "relative",
              height: 200,
              overflow: "hidden"
            }}
          >
            <div style={{ 
              position: "absolute",
              top: "16px",
              left: "16px",
              fontSize: 19,
              fontWeight: 600,
              fontStyle: "Semi Bold",
              color: isDark ? "#ffffff" : "#000",
              zIndex: 2
            }}>
              Total Logs
            </div>
            <div style={{ 
              position: "absolute",
              bottom: "20px",
              left: "16px",
              fontSize: 36, 
              fontWeight: 700, 
              fontStyle: "bold",
              color: isDark ? "#ffffff" : "#000",
              lineHeight: 1,
              zIndex: 1
            }}>
              {total}
            </div>
            <div style={{ 
              position: "absolute",
              bottom: "16px",
              right: "16px",
              fontSize: 12, 
              color: isDark ? "#73d13d" : "#52c41a",
              fontWeight: 500
            }}>
              +12%
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div
            style={{
              background: isDark ? "#2a2a3e" : "#fff",
              border: isDark ? "1px solid #3a3a4e" : "1px solid #000",
              borderRadius: 12,
              boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
              padding: "20px",
              position: "relative",
              height: 200,
              overflow: "hidden"
            }}
          >
            <div style={{ 
              position: "absolute",
              top: "16px",
              left: "16px",
              fontSize: 19,
              fontWeight: 600,
              fontStyle: "Semi Bold",
              color: isDark ? "#ffffff" : "#000",
              zIndex: 2
            }}>
              Today
            </div>
            <div style={{ 
              position: "absolute",
              bottom: "20px",
              left: "16px",
              fontSize: 36, 
              fontWeight: 700, 
              fontStyle: "bold",
              color: isDark ? "#ffffff" : "#000",
              lineHeight: 1,
              zIndex: 1
            }}>
              {stats.todayLogs}
            </div>
            <div style={{ 
              position: "absolute",
              bottom: "16px",
              right: "16px",
              fontSize: 12, 
              color: isDark ? "#73d13d" : "#52c41a",
              fontWeight: 500
            }}>
              +8%
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div
            style={{
              background: isDark ? "#2a2a3e" : "#fff",
              border: isDark ? "1px solid #3a3a4e" : "1px solid #000",
              borderRadius: 12,
              boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
              padding: "20px",
              position: "relative",
              height: 200,
              overflow: "hidden"
            }}
          >
            <div style={{ 
              position: "absolute",
              top: "16px",
              left: "16px",
              fontSize: 19,
              fontWeight: 600,
              fontStyle: "Semi Bold",
              color: isDark ? "#ffffff" : "#000",
              zIndex: 2
            }}>
              This Week
            </div>
            <div style={{ 
              position: "absolute",
              bottom: "20px",
              left: "16px",
              fontSize: 36, 
              fontWeight: 700, 
              fontStyle: "bold",
              color: isDark ? "#ffffff" : "#000",
              lineHeight: 1,
              zIndex: 1
            }}>
              {stats.thisWeekLogs}
            </div>
            <div style={{ 
              position: "absolute",
              bottom: "16px",
              right: "16px",
              fontSize: 12, 
              color: isDark ? "#ffc53d" : "#faad14",
              fontWeight: 500
            }}>
              -5%
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div
            style={{
              background: isDark ? "#2a2a3e" : "#fff",
              border: isDark ? "1px solid #3a3a4e" : "1px solid #000",
              borderRadius: 12,
              boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
              padding: "20px",
              position: "relative",
              height: 200,
              overflow: "hidden"
            }}
          >
            <div style={{ 
              position: "absolute",
              top: "16px",
              left: "16px",
              fontSize: 19,
              fontWeight: 600,
              fontStyle: "Semi Bold",
              color: isDark ? "#ffffff" : "#000",
              zIndex: 2
            }}>
              This Month
            </div>
            <div style={{ 
              position: "absolute",
              bottom: "20px",
              left: "16px",
              fontSize: 36, 
              fontWeight: 700, 
              fontStyle: "bold",
              color: isDark ? "#ffffff" : "#000",
              lineHeight: 1,
              zIndex: 1
            }}>
              {stats.thisMonthLogs}
            </div>
            <div style={{ 
              position: "absolute",
              bottom: "16px",
              right: "16px",
              fontSize: 12, 
              color: isDark ? "#ff7875" : "#ff4d4f",
              fontWeight: 500
            }}>
              -9%
            </div>
          </div>
        </Col>
      </Row>

      {/* Audit Logs Table */}
      <div style={{ marginTop: 48 }}>
        <Table
          columns={getColumns()}
          dataSource={auditLogs}
          rowKey="id"
          loading={loading}
          className="custom-table"
          expandable={{
            expandedRowRender: (record) => renderChanges(record.oldValues, record.newValues),
            expandIcon: ({ expanded, onExpand, record }) => {
              const changes = formatChanges(record.oldValues, record.newValues);
              return changes.length > 0 ? (
                <Button
                  type="text"
                  size="small"
                  onClick={(e) => onExpand(record, e)}
                  style={{
                    color: isDark ? '#1890ff' : '#1890ff',
                    fontSize: '12px',
                    padding: '4px 8px'
                  }}
                >
                  {expanded ? 'Hide Details' : `View ${changes.length} Changes`}
                </Button>
              ) : null;
            },
            rowExpandable: (record) => {
              const changes = formatChanges(record.oldValues, record.newValues);
              return changes.length > 0;
            },
          }}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page, pageSize) => {
              setPage(page);
              setPageSize(pageSize);
            },
          }}
          scroll={{ x: 800 }}
        />
      </div>
    </div>
  );

  const tabItems = [
    {
      key: "user-audit",
      label: (
        <span>
          <AuditOutlined />
          User Audit
        </span>
      ),
      children: renderAuditContent(),
    },
    {
      key: "issuer-audit",
      label: (
        <span>
          <BankOutlined />
          Issuer Audit
        </span>
      ),
      children: renderAuditContent(),
    },
    {
      key: "isin-audit",
      label: (
        <span>
          <FileTextOutlined />
          ISIN Audit
        </span>
      ),
      children: renderAuditContent(),
    },
  ];

  return (
    <div className={`admin-container ${isDark ? "dark" : ""}`}>
      <Card
        className="admin-card"
        style={{
          background: isDark ? "#1f1f1f" : "#fff",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Tabs
          activeKey={`${auditType}-audit`}
          onChange={(key) => {
            const type = key.replace('-audit', '');
            setAuditType(type);
            setPage(1); // Reset to first page when switching tabs
          }}
          items={tabItems}
          size="large"
          type="card"
          className="admin-management-tabs"
          tabBarStyle={{
            marginBottom: "24px",
            borderBottom: `2px solid ${isDark ? "#434343" : "#f0f0f0"}`,
          }}
        />
      </Card>
    </div>
  );
};

export default UnifiedAuditLogs;
